import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Checkbox } from "../components/ui/checkbox"
import { Label } from "../components/ui/label"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import {
  hasSourceVectorPayload,
  renderSourceVectorPayload,
  sourceFillBoxStyle,
} from "./source-derived-rendering"

/** Extract last segment from a bind path. E.g., "#{form.agree}" -> "agree" */
function extractFieldName(bind?: string): string | undefined {
  if (!bind) return undefined
  const stripped = bind.replace(/^#\{/, "").replace(/\}$/, "")
  const segments = stripped.split(".")
  return segments[segments.length - 1]
}

function humanizeFieldName(fieldName?: string): string | undefined {
  if (!fieldName) return undefined
  const readable = fieldName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
  if (!readable) return undefined
  return readable.charAt(0).toUpperCase() + readable.slice(1)
}

function resolveAccessibleText(
  value: string | undefined,
  resolveSymbol: (value: string) => unknown,
): string | undefined {
  if (!value) return undefined
  const resolved = resolveSymbol(value)
  const candidate = String(resolved ?? value).trim()
  if (!candidate || /^(?:#|@)\{.*\}$/.test(candidate)) return undefined
  return candidate
}

export function CheckboxRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext, designTokens } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // NOTE: element.variant === "checked" is a legacy initial-state fallback
  // for `fallbackChecked` below; the two code paths do not conflict because
  // the data-variant attr carries the theme-variant semantic (e.g. "default",
  // "outline") while `variant === "checked"` is a pre-v2 state marker that
  // gets coerced to "checked" in data-variant and falls back to fallbackChecked.
  const atomDefaults = useAtomDefaults("checkbox")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const authoredText = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  const authoredLabel = resolveAccessibleText(element.label, resolveSymbol)
  const text = authoredLabel ?? authoredText
  const explicitAriaLabel = resolveAccessibleText(element.ariaLabel, resolveSymbol)
  const fieldName = extractFieldName(element.bind)
  const accessibleName =
    explicitAriaLabel ||
    text?.trim() ||
    resolveAccessibleText(element.name, resolveSymbol) ||
    humanizeFieldName(fieldName) ||
    "Checkbox"
  const disabled = element.disabled === true || element.disabled === "true"
  const componentContract = (element as any).componentContract as
    | { variant?: { state?: string }; state?: string }
    | undefined
  const sourceState = String(
    componentContract?.variant?.state ??
      componentContract?.state ??
      (element as any).state ??
      "",
  ).toLowerCase()
  // React-standard props: defaultChecked (bool) or legacy variant/visible fallback
  const fallbackChecked =
    (element as any).defaultChecked === true ||
    (element as any).checked === true ||
    element.variant === "checked" ||
    sourceState === "checked" ||
    sourceState === "selected" ||
    sourceState === "on" ||
    resolveSymbol(String(element.visible ?? "")) === true

  const [checked, setChecked] = useBoundValue<boolean>(
    element.bind,
    resolveSymbol,
    fallbackChecked,
    Boolean,
  )

  const handleCheckedChange = (value: boolean) => {
    setChecked(value)
    // Forward the per-item `itemContext` (e.g. `{ task: item }`) alongside the
    // checked value — parity with ButtonRenderer (which dispatches itemContext on
    // tap). Without this, a checkbox inside a block-ref'd row loses its item
    // identity, so a flow predicate like `t.id == #{task.id}` resolves against an
    // undefined item and mutates nothing. For non-iterated checkboxes itemContext
    // is empty, so the payload stays `{ value }` (backward-compatible).
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value })
  }

  const sourceVector = hasSourceVectorPayload(element)
  const sourceControl = sourceVector ? (
    <button
      id={element.id}
      type="button"
      disabled={disabled}
      role="checkbox"
      aria-checked={checked}
      aria-label={accessibleName}
      data-state={checked ? "checked" : "unchecked"}
      data-testid={`checkbox-${element.id}`}
      data-action="toggle"
      data-field-name={fieldName}
      data-field-type="checkbox"
      data-atom-type="checkbox"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-cogo-source-vector-control="true"
      className={cn(stripPresetConflicts(element.className, undefined, element.designAuthorityClassName))}
      style={{
        ...sourceFillBoxStyle(element, designTokens),
        position: "relative",
        display: "grid",
        placeItems: "center",
        padding: 0,
        border: 0,
        background: "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        color: String((element as any).style?.color ?? "currentColor"),
      }}
      onClick={() => handleCheckedChange(!checked)}
    >
      {renderSourceVectorPayload(element, "cogo-source-checkbox-vector")}
    </button>
  ) : null

  // No label: return Checkbox directly (matches shadcn DOM)
  if (!text) {
    if (sourceControl) return sourceControl

    return (
      <Checkbox
        id={element.id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={handleCheckedChange}
        className={cn(stripPresetConflicts(element.className, undefined, element.designAuthorityClassName))}
        aria-label={accessibleName}
        data-testid={`checkbox-${element.id}`}
        data-action="toggle"
        data-field-name={fieldName}
        data-field-type="checkbox"
        data-atom-type="checkbox"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
      />
    )
  }

  if (sourceControl) {
    return (
      <div
        className={cn("flex items-center space-x-2")}
        data-testid={`checkbox-${element.id}-field`}
        data-action="toggle"
        data-field-name={extractFieldName(element.bind)}
        data-field-type="checkbox"
        data-atom-type="checkbox"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
        data-cogo-source-vector-field="true"
      >
        {sourceControl}
        <Label htmlFor={element.id}>{text}</Label>
      </div>
    )
  }

  return (
    <div className={cn("flex items-center space-x-2", stripPresetConflicts(element.className, undefined, element.designAuthorityClassName))} data-testid={`checkbox-${element.id}`} data-action="toggle" data-field-name={extractFieldName(element.bind)} data-field-type="checkbox" data-atom-type="checkbox" data-variant={resolvedVariant} data-size={resolvedSize}>
      <Checkbox id={element.id} checked={checked} disabled={disabled} onCheckedChange={handleCheckedChange} aria-label={accessibleName} />
      <Label htmlFor={element.id}>{text}</Label>
    </div>
  )
}
